import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../atoms/Card/Card';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { getJournalMilestones } from '../../services/journalAiService';
import { useTranslation } from '../../i18n/useTranslation';
import './JournalAi.css';

export const journalMilestonesQueryKey = (aquariumId: string) => ['journalMilestones', aquariumId] as const;

/**
 * Milestones of the tank (entries tagged "Milestone", "Tank Start", …),
 * oldest first, as a small timeline on the journal page. No AI.
 */
export function JournalMilestones({ aquariumId }: { aquariumId: string }) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: journalMilestonesQueryKey(aquariumId),
    queryFn: () => getJournalMilestones(aquariumId),
    enabled: Boolean(aquariumId),
  });

  let body;
  if (isLoading) {
    body = <LiveRegion><Text color="muted">{t('journal.milestones.loading')}</Text></LiveRegion>;
  } else if (isError) {
    body = <LoadErrorState title={t('journal.milestones.loadError')} titleLevel={3} onRetry={() => void refetch()} compact />;
  } else if (!data || data.length === 0) {
    body = <Text color="muted" data-testid="journal-milestones-empty">{t('journal.milestones.empty')}</Text>;
  } else {
    body = (
      <ol className="rg-journal-milestones" data-testid="journal-milestones-list">
        {data.map((m) => (
          <li key={m.id} className="rg-journal-milestones__item">
            <span className="rg-journal-milestones__dot" aria-hidden="true" />
            <Text as="span" size="sm" color="muted">
              <time dateTime={m.createdAt}>
                {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(m.createdAt))}
              </time>
            </Text>
            <Link className="rg-journal-milestones__link" to={`/aquariums/${aquariumId}/journal/${m.id}`}>
              {m.title}
            </Link>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <Card className="rg-journal-ai" data-testid="journal-milestones">
      <Heading level={2} variant="card">{t('journal.milestones.title')}</Heading>
      <Text color="secondary">{t('journal.milestones.description')}</Text>
      {body}
    </Card>
  );
}

export default JournalMilestones;
